import { ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import type { BrandingDeploymentGateStatus, BrandingLogosStatus } from '../services/api'

interface BrandingAssetStatusPanelProps {
  brandingLogos?: BrandingLogosStatus
  summaryLabel?: string
  showOnlyFailedAssets?: boolean
  assetIndent?: number
}

const BrandingAssetStatusPanel = ({
  brandingLogos,
  summaryLabel = 'Branding Logos',
  showOnlyFailedAssets = true,
  assetIndent = 6,
}: BrandingAssetStatusPanelProps) => {
  if (!brandingLogos) {
    return null
  }

  const visibleAssets = showOnlyFailedAssets
    ? brandingLogos.assets.filter((asset) => !asset.success || asset.isWarning)
    : brandingLogos.assets

  const formatGate = (label: string, gate?: BrandingDeploymentGateStatus) => {
    if (!gate) {
      return `${label}: not evaluated`
    }

    const outcome = gate.passed ? 'passed' : 'failed'
    const statusCode = gate.httpStatusCode ? ` (${gate.httpStatusCode})` : ''
    const attempts = gate.attemptCount > 0 ? ` after ${gate.attemptCount} attempt${gate.attemptCount === 1 ? '' : 's'}` : ''
    const detail = gate.errorDetail ? ` - ${gate.errorDetail}` : ''

    return `${label}: ${outcome}${statusCode}${attempts}${detail}`
  }

  const formatSecondary = (asset: BrandingLogosStatus['assets'][number]) => {
    const lines = [
      formatGate('PUT', asset.putAccepted),
      formatGate('Stream readback', asset.streamReadbackMatched),
      formatGate('Tenant confirmation', asset.tenantUiConfirmed),
    ]

    if (asset.delayedRecheckScheduled) {
      lines.push(
        asset.delayedRecheckCompleted
          ? `Delayed re-check completed (${asset.delayedRecheckAttemptCount} pass${asset.delayedRecheckAttemptCount === 1 ? '' : 'es'})`
          : 'Delayed re-check scheduled'
      )
    }

    if (asset.endpoint) {
      lines.push(`Endpoint: ${asset.endpoint}`)
    }

    return (
      <>
        {lines.map((line) => (
          <Typography key={`${asset.assetType}-${line}`} variant="body2" color="text.secondary" component="div">
            {line}
          </Typography>
        ))}
      </>
    )
  }

  return (
    <>
      <ListItem>
        <ListItemIcon>
          <InfoIcon color={brandingLogos.hasBlockingFailures ? 'error' : 'warning'} />
        </ListItemIcon>
        <ListItemText
          primary={`${summaryLabel}: ${brandingLogos.summaryMessage}`}
        />
      </ListItem>

      {visibleAssets.map((asset, index) => (
        <ListItem key={`branding-asset-${index}`} sx={{ pl: assetIndent }}>
          <ListItemIcon>
            {asset.isWarning ? <WarningIcon color="warning" /> : <ErrorIcon color="error" />}
          </ListItemIcon>
          <ListItemText
            primary={`${asset.assetType}: ${asset.errorMessage || (asset.success ? 'Verification completed with warnings' : 'Deployment failed')}`}
            secondary={formatSecondary(asset)}
          />
        </ListItem>
      ))}
    </>
  )
}

export default BrandingAssetStatusPanel